import { format } from 'date-fns';
import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import React, { useContext, useState, useEffect } from 'react';
import { MessagingContext } from '../../../services';
import { BaseInput, BaseButton, Toggle } from '../../base';
import { TagsList } from '../../tags';
import { AttachmentButton } from '../attachment-button';
import { SearchInput } from '../compose-panel';
import { NoteIndicator } from '../note-indicator';
import { MessageFields, IMessageFieldsValues } from '../message-fields';
import { ClaimsManager } from '../claims-manager';
import { isHtmlContent, sanitizeHtml, normalizeMarkdown } from '../util';
import * as S from './chat-body.styles';
import {BlockedComponent} from "./blocked-component";
import { CoreFetch } from '../../../api';
import { MessageApi } from '../../../api/server';

interface IChatBody {
    hideNote?: boolean;
}

export const ChatBody: React.FunctionComponent<IChatBody> = (props: IChatBody): React.ReactElement => {
    const { hideNote } = props;
    
    // Get context and data BEFORE any hooks
    const context = useContext(MessagingContext);
    const comment = _.first(context.comments);
    const isReadOnlyClaimChoice = context.isReadOnlyClaimChoice;
    const isSupportApp = context.account?.supportGrant === true;
    
    // Parse ISO date string to just date portion (yyyy-MM-dd)
    const parseDateOnlyInitial = (isoString: string | undefined | null): string | undefined => {
        if (!isoString) return undefined;
        return isoString.split('T')[0];
    };

    // All useState hooks MUST be called before any conditional returns
    const [isEditable, setEditable] = useState(false);
    const [isSubjectLoading, setSubjectLoading] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const [subjectView, setSubjectView] = useState(context.message?.subject || '');
    const [messageFields, setMessageFields] = useState<IMessageFieldsValues>({
        priority: context.message.priority || undefined,
        dueDate: parseDateOnlyInitial(context.message.dueDate),
        categoryIds: _.map(context.message.links?.messageCategories, 'id') || [],
        assignedUserIds: _.map(context.message.links?.users, 'id') || [],
    });

    // NOW we can do conditional returns AFTER all hooks are declared
    if (!context.message || !comment) {
        return <></>;
    }

    const fmtDate = (d: Date): string => format(d, 'MMM d, yyyy');
    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());
    
    // Parse ISO date string to just date portion (yyyy-MM-dd)
    const parseDateOnly = (isoString: string | undefined | null): string | undefined => {
        if (!isoString) return undefined;
        // Extract just the date part from ISO string (e.g., '2025-10-21T00:00:00Z' -> '2025-10-21')
        return isoString.split('T')[0];
    };

    // NB: test both ProviderComment and SupportComment, IFF not hiding notes
    const hasNote = !hideNote && _.get(comment, 'note', false);

    const { subject, created, id, links, dueDate, priority } = context.message;
    const claimIds = _.map(links?.claims, 'id') || [];
    const documents = _.filter(context.documents, document => {
        const commentDocs = _.get(comment, 'documents', []);
        return _.includes(commentDocs, document.id);
    });
    const categoryNames = _.map(links?.messageCategories, cat => context.getCategoryName ? context.getCategoryName(cat.id) || undefined : undefined).filter(name => name !== undefined);
    const assigneeNames = _.map(links?.users, user => context.getUserName ? context.getUserName(user.id) || undefined : undefined).filter(name => name !== undefined);
    console.log('categoryNames', categoryNames);
    const renderAttachments = (): React.ReactElement[] | undefined => {
        if (!documents || !documents.length) {
            return;
        }
        return _.times(
            _.size(documents),
            (i: number): React.ReactElement => {
                const document = documents[i];
                const onDownload = async (): Promise<void> => {
                    if (document && document.id) {
                        await (context.onDocumentDownload && context.onDocumentDownload(document.id));
                    }
                };

                const onAssign = async (): Promise<void> => {
                    await (context.onDocumentAssign && context.onDocumentAssign(document));
                };

                const onEdit = async (): Promise<void> => {
                    await (context.onDocumentEdit && context.onDocumentEdit(document));
                };

                const canAssign = claimIds.length > 0 && !document.claimId && !!context.onDocumentAssign;

                return (
                    <AttachmentButton
                        key={`attachment-${i}`}
                        filename={document.filename as string}
                        filesize={document.filesize}
                        thumbnail={document.thumbnail}
                        onDownload={onDownload}
                        onAssign={canAssign ? onAssign : undefined}
                        onEdit={context.onDocumentEdit ? onEdit : undefined}
                        isSupportApp={isSupportApp}
                    />
                );
            },
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
    };

    const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
        if (!id) {
            throw new Error("Can't edit subject for this message");
        }
        setSubjectLoading(true);
        await context.onSubjectUpdate(id, e.target.value);
        setSubjectLoading(false);
        handleToggleEditable();
    };

    const handleToggleEditable = () => {
        if (!context.isSubjectEditable) {
            return;
        }

        setEditable(!isEditable);
    };

    const handleRemoveClaim = (claimId: string) => async () => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }

        try {
            await context.onUnlinkEntityFromMessage?.(id, 'CLAIM', [claimId]);
        } catch (error) {
            console.error('Failed to unlink claim:', error);
        }
    };

    const handleChangeClaim = async (claimId: string) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }
        await context.onLinkEntityToMessage?.(id, 'CLAIM', [claimId]);
    };

    const handleBulkClaimsAdd = async (claimIds: string[]) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }
        await context.onLinkEntityToMessage?.(id, 'CLAIM', claimIds);
    };

    const renderClaimButtons = (): React.ReactElement[] => {
        return _.map(claimIds, (claimId: string) => (
            <S.ClaimButton
                key={claimId}
                claimId={claimId}
                onRemove={handleRemoveClaim(claimId)}
                forceClaim={!!isReadOnlyClaimChoice}
            />
        ));
    };

    const handleMessageFieldsChange = (newValues: IMessageFieldsValues) => {
        setMessageFields(newValues);
    };

    const handleUpdate = async () => {
        if (!id) {
            console.error("Can't update message without id");
            return;
        }

        setIsUpdateLoading(true);

        // Handle assignees (for both support and provider apps)
        try {
            const existingUserIds = _.map(context.message.links?.users, 'id') || [];
            const existingUserIdsSet = new Set(existingUserIds);
            const selectedUserIds = messageFields.assignedUserIds || [];
            
            const newUserIds = selectedUserIds.filter(userId => !existingUserIdsSet.has(userId));
            if (newUserIds.length > 0) {
                await context.onLinkEntityToMessage?.(id, 'BANK_USER', newUserIds);
            }

            const selectedUserIdsSet = new Set(selectedUserIds);
            const removedUserIds = existingUserIds.filter(userId => !selectedUserIdsSet.has(userId));
            if (removedUserIds.length > 0) {
                await context.onUnlinkEntityFromMessage?.(id, 'BANK_USER', removedUserIds);
            }
        } catch (error) {
            console.error('Failed to update assignees:', error);
        }

        // Handle categories (for both support and provider apps)
        try {
            const existingCategoryIds = _.map(context.message.links?.messageCategories, 'id') || [];
            const existingCategoryIdsSet = new Set(existingCategoryIds);
            const selectedCategoryIds = messageFields.categoryIds || [];
            
            const newCategoryIds = selectedCategoryIds.filter(catId => !existingCategoryIdsSet.has(catId));
            if (newCategoryIds.length > 0) {
                await context.onLinkEntityToMessage?.(id, 'MESSAGE_CATEGORY', newCategoryIds);
            }

            const selectedCategoryIdsSet = new Set(selectedCategoryIds);
            const removedCategoryIds = existingCategoryIds.filter(catId => !selectedCategoryIdsSet.has(catId));
            if (removedCategoryIds.length > 0) {
                await context.onUnlinkEntityFromMessage?.(id, 'MESSAGE_CATEGORY', removedCategoryIds);
            }
        } catch (error) {
            console.error('Failed to update categories:', error);
        }

        // Handle priority and due date
        try {
            const previousPriority = context.message.priority;
            const newPriority = messageFields.priority;
            const didPriorityChange = newPriority !== previousPriority;
            
            // Compare just the date portions (yyyy-MM-dd)
            const previousDueDateString = parseDateOnly(context.message.dueDate);
            const newDueDateString = messageFields.dueDate;
            const didDueDateChange = newDueDateString !== previousDueDateString;
            
            if (didPriorityChange || didDueDateChange) {
                // Convert yyyy-MM-dd to ISO format for API
                const dueDateISO = newDueDateString ? new Date(newDueDateString + 'T00:00:00Z').toISOString() : null;
                
                await CoreFetch(MessageApi).updateSupportMessage({
                    id,
                    supportUpdateMessagePayload: {
                        priority: newPriority as any,
                        dueDate: dueDateISO || undefined
                    }
                });
            }
        } catch (error) {
            console.error('Failed to update priority/due date:', error);
        }

        setIsUpdateLoading(false);
        console.log('Message update completed');
    };

    // Update subjectView when message subject changes
    useEffect(() => {
        if (context.message?.subject) {
            setSubjectView(context.message.subject);
        }
    }, [context.message?.subject]);

    // Update message fields when context.message changes
    useEffect(() => {
        setMessageFields({
            priority: context.message?.priority || undefined,
            dueDate: parseDateOnly(context.message?.dueDate),
            categoryIds: _.map(context.message?.links?.messageCategories, 'id') || [],
            assignedUserIds: _.map(context.message?.links?.users, 'id') || [],
        });
    }, [context.message?.id, context.message?.priority, context.message?.dueDate, context.message?.links?.messageCategories, context.message?.links?.users]);


    return (
        <S.Header note={hasNote}>
            <S.Row>
                <S.Timestamp>{fmtDate(parseDate(created))}</S.Timestamp>
                {!hideNote && <NoteIndicator comment={comment} />}
            </S.Row>
            {/* <S.Topic>{patientFirst} {datesOfService}</S.Topic> */}
            {isEditable ? (
                <S.EditableSubject title="Edit">
                    <BaseInput
                        onChange={handleChange}
                        disabled={isSubjectLoading}
                        onBlur={handleBlur}
                        value={subjectView}
                        autoFocus={true}
                    />
                </S.EditableSubject>
            ) : (
                <S.Subject onClick={handleToggleEditable}>{subjectView}</S.Subject>
            )}
            {categoryNames && categoryNames.length > 0 && (
                <S.Row style={{ marginTop: '8px', marginBottom: '4px' }}>
                    <S.TagsSection>
                        <S.TagsLabel>Categories</S.TagsLabel>
                        <TagsList 
                            items={categoryNames}
                            emptyText="" 
                        />
                    </S.TagsSection>
                </S.Row>
            )}
            {assigneeNames && assigneeNames.length > 0 && (
                <S.Row style={{ marginTop: '4px', marginBottom: '8px' }}>
                    <S.TagsSection>
                        <S.TagsLabel>Assignees</S.TagsLabel>
                        <TagsList 
                            items={assigneeNames}
                            emptyText="" 
                        />
                    </S.TagsSection>
                </S.Row>
            )}
            <ClaimsManager
                messageId={id}
                claimIds={claimIds}
                onClaimAdd={handleChangeClaim}
                onBulkClaimsAdd={handleBulkClaimsAdd}
                onClaimRemove={(claimId: string) => handleRemoveClaim(claimId)()}
            />
            <S.Body>
                {isHtmlContent(comment.body) ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
                ) : (
                    <Markdown options={{
                        overrides: {
                            iframe: {
                                component: BlockedComponent
                            },
                        },
                    }}>{normalizeMarkdown(comment.body || '')}</Markdown>
                )}
            </S.Body>
            <S.Attachments>{renderAttachments()}</S.Attachments>
            <MessageFields 
                values={messageFields}
                onChange={handleMessageFieldsChange}
            />
            <S.UpdateButtonRow>
                <BaseButton 
                    onClick={handleUpdate}
                    disabled={isUpdateLoading}
                    variant="primary"
                >
                    {isUpdateLoading ? 'Updating...' : 'Update'}
                </BaseButton>
            </S.UpdateButtonRow>
        </S.Header>
    );
};
